import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';

const UserEditForm = ({ isOpen, onClose, user, currentUser, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'regular_user',
    is_active: true,
    is_verified: false,
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        email: user.email,
        password: '',
        confirmPassword: '',
        role: user.role,
        is_active: user.is_active,
        is_verified: user.is_verified,
      });
      setErrors({});
      setActiveTab('basic');
      loadUserPermissions();
    }
  }, [isOpen, user]);

  const loadUserPermissions = async () => {
    setLoadingPermissions(true);
    const result = await userService.getUserPermissions(user.id);

    if (result.success) {
      setPermissions(result.permissions.table_permissions || []);
    } else {
      console.error('Failed to load user permissions:', result.error);
    }
    setLoadingPermissions(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitBasicInfo = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const updatePayload = {
        email: formData.email,
        is_active: formData.is_active,
        is_verified: formData.is_verified,
      };

      if (formData.password) {
        updatePayload.password = formData.password;
      }

      // Update basic user info via FastAPI-Users endpoint
      const userUpdateResult = await userService.updateUser(user.id, updatePayload);

      // Update role separately if it changed and user is not editing themselves
      if (formData.role !== user.role && user.id !== currentUser.id) {
        const roleUpdateResult = await userService.updateUserRole(user.id, formData.role);

        if (!roleUpdateResult.success) {
          setErrors({ general: roleUpdateResult.error });
          setLoading(false);
          return;
        }
      }

      if (userUpdateResult.success) {
        onUserUpdated();
      } else {
        setErrors({ general: userUpdateResult.error });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePermission = async (permissionId) => {
    if (window.confirm('Are you sure you want to remove this table permission?')) {
      const result = await userService.deleteTablePermission(permissionId);

      if (result.success) {
        loadUserPermissions(); // Refresh permissions list
      } else {
        setErrors({ general: result.error });
      }
    }
  };

  const formatPermissionRights = (permission) => {
    const rights = [];
    if (permission.can_insert) rights.push('Insert');
    if (permission.can_delete) rights.push('Delete');
    return rights.length > 0 ? rights.join(', ') : 'No permissions';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal user-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit User: {user?.email}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {errors.general && (
            <div className="notification error">
              {errors.general}
            </div>
          )}

          <div className="user-edit-tabs">
            <button
              type="button"
              className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              Basic Info
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'permissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('permissions')}
            >
              Table Permissions
            </button>
          </div>

          {activeTab === 'basic' && (
            <form className="user-form" onSubmit={handleSubmitBasicInfo}>
              <div className="edit-section">
                <h4>User Information</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="role">Role</label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      disabled={user.id === currentUser.id}
                      title={user.id === currentUser.id ? 'Cannot change your own role' : ''}
                    >
                      <option value="regular_user">Regular User</option>
                      <option value="superuser">Superuser</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        disabled={user.id === currentUser.id}
                      />
                      <label htmlFor="is_active">Active</label>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="is_verified"
                        name="is_verified"
                        checked={formData.is_verified}
                        onChange={handleChange}
                      />
                      <label htmlFor="is_verified">Verified</label>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">New Password</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Leave blank to keep current password"
                      autoComplete="new-password"
                    />
                    {errors.password && <span className="error-text">{errors.password}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                    />
                    {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'permissions' && (
            <div className="edit-section">
              <h4>Table Permissions</h4>

              {loadingPermissions ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading permissions...</p>
                </div>
              ) : (
                <div className="permissions-list">
                  {permissions.length > 0 ? (
                    permissions.map((permission) => (
                      <div key={permission.id} className="permission-item">
                        <div className="permission-details">
                          <div className="permission-table">{permission.table_name}</div>
                          <div className="permission-rights">
                            {formatPermissionRights(permission)}
                          </div>
                        </div>
                        <div className="permission-actions">
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleDeletePermission(permission.id)}
                            title="Remove permission"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <p>No table permissions assigned to this user.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserEditForm;
